import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviderSettings } from '../../Services/ProviderService';
import { toast } from 'react-toastify';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import CustomDatePicker from '../Common/CustomDatePicker';

const EVENT_TYPE_SETTING_TYPE_ID = 41;
const STATUS_OPTIONS = [
  { id: 1, label: 'Active' },
  { id: 2, label: 'Inactive' },
];

const emptyForm = {
  id: null,
  provider_id: null,
  status_id: null,
  event_type: null,
  description: '',
  case_text: '',
  event_date: null,
  date_reported: null,
  end_date: null,
  carrier_name: '',
  amount_paid: '',
  type: 'create',
};

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? item.Value ?? item.value ?? ''),
});

const validationSchema = Yup.object({
  status_id: Yup.number().nullable(),
  event_type: Yup.number().nullable(),
  description: Yup.string().trim().nullable(),
  case_text: Yup.string().trim().nullable(),
  event_date: Yup.date().nullable().typeError('Invalid date format'),
  date_reported: Yup.date().nullable().typeError('Invalid date format'),
  end_date: Yup.date().nullable().typeError('Invalid date format'),
  carrier_name: Yup.string().trim().nullable(),
  amount_paid: Yup.number().nullable(),
  type: Yup.string().trim().nullable(),
});

export default function EventLogModal({ isOpen, providerId, onClose, onSave, initialData, isLoading }) {
  const { token } = useAuth();
  const [providerSettings, setProviderSettings] = useState({ eventType: [] });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const initialValues = useMemo(() => {
    const base = initialData ? { ...emptyForm, ...initialData } : { ...emptyForm, provider_id: providerId };

    return {
      ...base,
      provider_id: base.provider_id || providerId || null,
      event_type: initialData?.event_type ?? null,
      type: initialData?.type || (initialData?.id ? 'update' : 'create'),
    };
  }, [initialData, providerId]);

  const loadProviderSettings = async () => {
    if (!token) return;

    setSettingsLoading(true);
    try {
      const response = await getAllProviderSettings(token, EVENT_TYPE_SETTING_TYPE_ID);
      if (response?.is_success) {
        setProviderSettings({ eventType: Array.isArray(response.data) ? response.data.map(mapProviderSettingItem) : [] });
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to load event type settings');
      }
    } catch (error) {
      console.error('Failed to load event type settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    loadProviderSettings();
  }, [token]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        id: values.id || null,
        event_type: values.event_type !== '' && values.event_type !== null && values.event_type !== undefined ? Number(values.event_type) : null,
        status_id: values.status_id !== '' && values.status_id !== null && values.status_id !== undefined ? Number(values.status_id) : null,
        amount_paid: values.amount_paid !== '' && values.amount_paid !== null && values.amount_paid !== undefined ? Number(values.amount_paid) : null,
        event_date: values.event_date || null,
        date_reported: values.date_reported || null,
        end_date: values.end_date || null,
        type: values.id ? 'update' : 'create',
      };
      onSave(payload);
    },
  });

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">
              {initialData?.id ? 'Edit Event Log' : 'Add Event Log'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>

          <form onSubmit={formik.handleSubmit}>
            <input type="hidden" name="provider_id" value={formik.values.provider_id || ''} />
            <input type="hidden" name="status_id" value={formik.values.status_id || ''} />
            <input type="hidden" name="type" value={formik.values.type || ''} />

            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-semibold hc-text-primary mb-3">Event Details</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <SearchableDropdown
                      label="Event Type"
                      placeholder="Select Option"
                      options={providerSettings.eventType}
                      value={formik.values.event_type ?? ''}
                      onChange={(value) => {
                        formik.setFieldValue('event_type', value || null);
                        formik.setFieldTouched('event_type', true);
                      }}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      className={formik.touched.event_type && formik.errors.event_type ? 'is-invalid' : ''}
                      emptyMessage="No event types found"
                    />
                    {formik.touched.event_type && formik.errors.event_type ? (
                      <div className="invalid-feedback d-block">{formik.errors.event_type}</div>
                    ) : null}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Status</label>
                    <select
                      name="status_id"
                      value={formik.values.status_id ?? ''}
                      onChange={(event) => formik.setFieldValue('status_id', event.target.value ? Number(event.target.value) : null)}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select status</option>
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Description</label>
                    <textarea
                      {...formik.getFieldProps('description')}
                      className="form-control rounded-3 shadow-sm"
                      rows={3}
                      placeholder="Enter event description"
                    />
                  </div>

                  <div className="col-lg-4 col-md-6">
                    <label className="form-label fw-semibold">Event Date</label>
                    <CustomDatePicker
                      formik={formik}
                      {...formik.getFieldProps('event_date')}
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('event_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <label className="form-label fw-semibold">Date Reported</label>
                    <CustomDatePicker
                      formik={formik}
                      {...formik.getFieldProps('date_reported')}
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('date_reported')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>

                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">End Date</label>
                    <CustomDatePicker
                    formik={formik}
                      {...formik.getFieldProps('end_date')}
                    />
                    {/* <input
                      type="date"
                      {...formik.getFieldProps('end_date')}
                      className="form-control rounded-3 shadow-sm"
                    /> */}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Carrier Name</label>
                    <input
                      {...formik.getFieldProps('carrier_name')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter carrier name"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold">Amount Paid</label>
                    <input
                      type="number"
                      min="0"
                      {...formik.getFieldProps('amount_paid')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter amount paid"
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">Case</label>
                    <textarea
                      {...formik.getFieldProps('case_text')}
                      className="form-control rounded-3 shadow-sm"
                      rows={3}
                      placeholder="Enter case notes"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4 d-flex align-items-center justify-content-center" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
